import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Country from './Country';
import {DataService, ActionService} from '@hit/base/src';

export default class Project extends BaseModel {
  constructor({
    id,
    number,
    designation,
    active = true,
    _tags = [],
    street = undefined,
    houseNo = undefined,
    boxNo = undefined,
    postcode = undefined,
    city = undefined,
    country = undefined,
    location = undefined,
    memo = undefined,
    calculatedLocation = false,
    customData = undefined,
    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
    nssoNo = undefined,
    nssoCheckInAtWork = false,
    individualAddress = false,
  }) {
    super(id, number || no, designation);
    this.active = active;
    this.street = street;
    this.houseNo = houseNo;
    this.boxNo = boxNo;
    this.postcode = postcode;
    this.city = city;
    this.country = country ? new Country(country) : null;
    this.memo = memo;
    this.location = location;
    this.calculatedLocation = calculatedLocation;
    this.customData = customData;
    this.nssoNo = nssoNo;
    this.nssoCheckInAtWork = nssoCheckInAtWork;
    this.individualAddress = individualAddress;
  }

  static get baseModuleForRedirection() {
    return 'project-base';
  }
  get baseModuleForRedirection() {
    return Project.baseModuleForRedirection;
  }

  static get fkName() {
    return 'project_id';
  }
  static get apiRoute() {
    return 'project';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }
  static get detailPageRoute() {
    return 'projectDetail';
  }
  static get listPageRoute() {
    return 'projectList';
  }
  get fkName() {
    return Project.fkName;
  }
  get apiRoute() {
    return Project.apiRoute;
  }
  get listPageRoute() {
    return Project.listPageRoute;
  }
  get detailPageRoute() {
    return Project.detailPageRoute;
  }
  static get attrsToStoreInForm() {
    return ['number', 'designation'];
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }

  static get joinAttrsKey() {
    return ['number', 'designation'];
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'desc',
    };
  }

  get defaultSort() {
    return Project.defaultSort;
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'customers',
      'street',
      'houseNo',
      'boxNo',
      'postcode',
      'city',
      'country',
      'active',
      'tags',
      'creationDate',
      'modificationDate',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'designation',
        'customers',
        'postcode',
        'city',
        'active',
        'tags',
      ],
      ['street', 'houseNo', 'boxNo', 'country', 'nssoNo', 'nssoCheckInAtWork'],
    );
  }

  /**
   * Designation for fast mode browser when searching by locality: "Code poste - Localité" (uppercase, unique key).
   */
  get fastModeBrowserDesignationPostcodeCity() {
    const parts = [this.postcode, this.city]
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v).trim().toUpperCase());
    return (
      parts.join(' - ') || (this.fullDesignation ?? '').toUpperCase() || ''
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'street',
        'houseNo',
        'boxNo',
        'postcode',
        'city',
        'country',
        'active',
        'tags',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }

  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '1fr'}),
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '2fr'}),
      new AT.StringAttribute({
        name: 'customers',
        column: 'customer',
        tableProperties: {
          header: 'hit-app.common.customers',
          maxWidth: '2fr',
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
      this.TAGS_ATTRIBUTE({moduleUsage: 'project', tableOrigin: 'project'}),
      new AT.StringAttribute({
        name: 'street',
        tableProperties: {
          header: 'hit-app.common.street',
        },
      }),
      new AT.StringAttribute({
        name: 'houseNo',
        column: 'house_no',
        tableProperties: {
          header: 'hit-app.common.house-number',
        },
      }),
      new AT.StringAttribute({
        name: 'boxNo',
        column: 'box_no',
        tableProperties: {
          header: 'hit-app.common.box-number',
        },
      }),
      new AT.StringAttribute({
        name: 'postcode',
        tableProperties: {
          header: 'hit-app.common.postcode',
        },
      }),
      new AT.StringAttribute({
        name: 'city',
        tableProperties: {
          header: 'hit-app.common.city',
        },
      }),
      new AT.FkCountryAttribute({
        name: 'country',
        joinPath: ['country!fk_project_country_country'],
        tableProperties: {
          header: 'hit-base.common.country',
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.LocationAttribute({
        name: 'location',
        tableProperties: {
          header: 'hit-base.common.location',
        },
      }),
      new AT.BooleanAttribute({
        name: 'calculatedLocation',
        column: 'calculated_location',
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'nssoNo',
        column: 'nsso_no',
        tableProperties: {
          header: 'hit-app.common.nsso-no',
        },
      }),
      new AT.BooleanAttribute({
        name: 'nssoCheckInAtWork',
        column: 'nsso_check_in_at_work',
        tableProperties: {
          header: 'hit-app.common.nsso-check-in-at-work',
        },
      }),
      new AT.BooleanAttribute({
        name: 'individualAddress',
        column: 'individual_address',
      }),
    ];
  }

  /**
   * PROJECT UTILITY METHODS INDEPENDANT FROM THE ATTRIBUTES DATA OF THE MODEL DEFINED ABOVE
   */

  /**
   * Returns the GPS position for the project. Either the value from the database. If not present, we calculate the value starting from the physical address
   * @param {*} updateLocationInDatabase : flag to update the location in the database
   * @returns the GPS position for the project
   */
  async getGpsPosition(updateLocationInDatabase = false) {
    if (this.location) return this.location;
    const gpsPosition = await Project.calculateGpsPosition(
      this.street,
      this.houseNo,
      this.postcode,
      this.city,
      this.country?.designation,
    );
    if (gpsPosition && updateLocationInDatabase) {
      await DataService.update(
        this.apiRoute,
        {id: `eq.${this.id}`},
        {location: gpsPosition},
      );
    }
    return gpsPosition;
  }

  /**
   * Starting from the physical address, we calculate (via action API) the GPS position for the address.
   * @param {*} street: the street of the address
   * @param {*} houseNo: the house number of the address
   * @param {*} postcode: the postcode of the address
   * @param {*} city: the city of the address
   * @param {*} country: the country of the address
   * @returns the GPS position for the address
   */
  static async calculateGpsPosition(street, houseNo, postcode, city, country) {
    const response = await ActionService.post('map/geocode', {
      values: {street, houseNo, postcode, city, country},
    });
    if (response?.data?.coordinates) {
      return {
        type: 'Point',
        coordinates: [
          response.data.coordinates.lng,
          response.data.coordinates.lat,
        ],
      };
    }
    return null;
  }

  static generateGoogleMapsUrl(gpsPosition, forIos = false) {
    if (gpsPosition?.type === 'Point') {
      const [lng, lat] = gpsPosition.coordinates;
      return forIos
        ? `maps://?q=${lat},${lng}`
        : `https://maps.google.com/?q=${lat},${lng}`;
    }
    throw new Error(
      'Google Maps URL cannot be generated because the GPS position is not valid',
    );
  }
}
